import { useCallback, useEffect, useMemo, useState } from 'react'

const DEFAULT_API = 'https://festool-api.main.awd.relaref.com'

// Must match the host shell's STORAGE_KEYS.accessToken. The shell writes the
// access token there on login and on every refresh, so re-reading it per request
// keeps a long-lived session working even when the refreshed token never reaches
// us as a React prop.
const TOKEN_STORAGE_KEY = 'kc_token'

type Item = {
  id: string
  source_name?: string | null
  url: string
  title: string
  price?: number | null
  currency?: string | null
  location?: string | null
  thumbnail_url?: string | null
  image_urls?: string[] | null
  condition?: string | null
  canonical_model_key?: string | null
  distance_min?: number | null
  distance_text?: string | null
  ai_profit?: number | null
  ai_confidence?: string | null
  ai_reason?: string | null
  new_price?: number | null
  new_price_source?: string | null
  retailer_url?: string | null
  avg_2dehands?: number | null
  avg_marktplaats?: number | null
  discount_pct?: number | null
  seen_at?: string | null
}

type Stats = {
  active_listings?: number
  catalog_models?: number
  priced_models?: number
  sources?: string[]
}

type Sort = 'profit' | 'distance' | 'recent'

type LB = { imgs: string[]; i: number; title: string; url: string }

const nf = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 0 })
const eur = (n?: number | null) => (n == null ? null : '€' + nf.format(Math.round(n)))

const confKey = (c?: string | null) => {
  const s = (c || '').toLowerCase()
  if (s.startsWith('hoog')) return 'hoog'
  if (s.startsWith('gem')) return 'gemid'
  if (s.startsWith('laag')) return 'laag'
  return ''
}
const confLabel: Record<string, string> = { hoog: 'hoog', gemid: 'gemid.', laag: 'laag' }

const condLabel: Record<string, string> = {
  new: 'nieuw', as_new: 'zo goed als nieuw', good: 'goed',
  used: 'gebruikt', for_parts: 'onderdelen', refurbished: 'refurbished',
}

const srcShort = (s?: string | null) => (s || '').replace('.be', '').replace('.nl', '')
const isMkt = (s?: string | null) => (s || '').startsWith('marktplaats')

// Full photo gallery for a listing: prefer the scraped image_urls array,
// fall back to the single thumbnail so navigation still degrades gracefully.
const galleryOf = (it: Item): string[] => {
  const a = (it.image_urls || []).filter(Boolean) as string[]
  if (a.length) return a
  return it.thumbnail_url ? [it.thumbnail_url] : []
}

const prettyModel = (k?: string | null) =>
  (k || '')
    .split('-')
    .map((p) => (p.length <= 3 ? p.toUpperCase() : p.charAt(0).toUpperCase() + p.slice(1)))
    .join(' ')

const svg = (children: React.ReactNode, size = 18) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">{children}</svg>
)
const Ic = {
  dash: svg(<><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>),
  list: svg(<><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><circle cx="3.6" cy="6" r="1.1" /><circle cx="3.6" cy="12" r="1.1" /><circle cx="3.6" cy="18" r="1.1" /></>),
  trend: svg(<><polyline points="3 17 9 11 13 15 21 7" /><polyline points="15 7 21 7 21 13" /></>),
  tag: svg(<><path d="M20.6 13.4 13 21a2 2 0 0 1-2.8 0l-7-7A2 2 0 0 1 2.6 12.6V4a1.4 1.4 0 0 1 1.4-1.4h8.6a2 2 0 0 1 1.4.6l6.6 6.6a2 2 0 0 1 0 2.8Z" /><circle cx="7.5" cy="7.5" r="1.4" /></>),
  globe: svg(<><circle cx="12" cy="12" r="9" /><line x1="3" y1="12" x2="21" y2="12" /><path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z" /></>),
  gear: svg(<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 6.6 19l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.6 1.6 0 0 0 3 13.4H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 6.6l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.6 1.6 0 0 0 10 3.6V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 2.7 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8Z" /></>),
  search: svg(<><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.5" y2="16.5" /></>, 16),
  refresh: svg(<><path d="M21 12a9 9 0 1 1-3-6.7" /><polyline points="21 3 21 9 15 9" /></>, 17),
  grid: svg(<><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>, 15),
  rows: svg(<><line x1="4" y1="7" x2="20" y2="7" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="17" x2="20" y2="17" /></>, 15),
  car: svg(<><path d="M3 12.5 5 7.5A2 2 0 0 1 6.9 6.2h10.2A2 2 0 0 1 19 7.5l2 5" /><path d="M2.5 12.5h19v4a1 1 0 0 1-1 1h-1.2a2 2 0 0 1-3.9 0H8.6a2 2 0 0 1-3.9 0H3.5a1 1 0 0 1-1-1v-4Z" /><circle cx="7" cy="17" r="1.4" /><circle cx="17" cy="17" r="1.4" /></>, 14),
  layers: svg(<><polygon points="12 2.5 22 8.2 12 14 2 8.2 12 2.5" /><polyline points="2 14.5 12 20 22 14.5" /></>, 24),
  box: svg(<><path d="M21 8 12 3 3 8v8l9 5 9-5V8Z" /><path d="M3 8l9 5 9-5" /><line x1="12" y1="13" x2="12" y2="21" /></>, 24),
  euro: svg(<><circle cx="12" cy="12" r="9" /><path d="M16 8.2A4.5 4.5 0 1 0 16 15.8" /><line x1="6.5" y1="11" x2="13" y2="11" /><line x1="6.5" y1="13.5" x2="12.3" y2="13.5" /></>, 22),
  zap: svg(<><polygon points="13 2 4 14 11 14 10 22 20 9 13 9 13 2" /></>, 22),
  chevL: svg(<polyline points="15 18 9 12 15 6" />, 22),
  chevR: svg(<polyline points="9 18 15 12 9 6" />, 22),
  close: svg(<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>, 20),
  ext: svg(<><path d="M14 3h7v7" /><path d="M21 3 11 13" /><path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" /></>, 14),
}

const nav: Array<[string, React.ReactNode, boolean]> = [
  ['Dashboard', Ic.dash, true], ['Listings', Ic.list, false], ['Winstkansen', Ic.trend, false],
  ['Prijscatalogus', Ic.tag, false], ['Bronnen', Ic.globe, false], ['Instellingen', Ic.gear, false],
]

const CSS = `
.ftl-app{min-height:100vh;background:#f0f2f5;color:#344767;font-family:'Roboto','Helvetica Neue',Arial,system-ui,sans-serif}
.ftl-app *{box-sizing:border-box}
.layout{display:flex}
.side{position:fixed;top:16px;left:16px;bottom:16px;width:236px;background:#fff;border-radius:16px;box-shadow:0 6px 20px rgba(0,0,0,.08);padding:16px;z-index:20}
.brand{display:flex;align-items:center;gap:12px;padding:6px 8px 16px;border-bottom:1px solid #e9ecef;margin-bottom:14px}
.brand .lg{width:34px;height:34px;border-radius:9px;background:linear-gradient(195deg,#66BB6A,#43A047);color:#fff;display:grid;place-items:center;font-weight:800;box-shadow:0 6px 14px rgba(67,160,71,.4)}
.brand b{font-size:14px;letter-spacing:.3px}.brand small{display:block;color:#67748e;font-size:11px}
.nav a{display:flex;align-items:center;gap:12px;padding:9px 12px;border-radius:10px;color:#67748e;font-size:14px;font-weight:500;text-decoration:none;margin-bottom:3px;cursor:pointer}
.nav a.on{background:#fff;box-shadow:0 4px 12px rgba(0,0,0,.1);color:#344767;font-weight:600}
.nav a .ib{width:32px;height:32px;border-radius:8px;background:#fff;display:grid;place-items:center;color:#7b8ab8;box-shadow:0 2px 6px rgba(0,0,0,.08)}
.nav a.on .ib{background:linear-gradient(195deg,#66BB6A,#43A047);color:#fff;box-shadow:0 4px 10px rgba(67,160,71,.4)}
.main{margin-left:276px;padding:18px 24px 28px;flex:1;min-width:0}
.top{display:flex;align-items:center;margin-bottom:6px;gap:12px;flex-wrap:wrap}
.crumb{font-size:12px;color:#67748e}.crumb b{color:#344767}.h1{font-size:20px;font-weight:700;margin-top:2px}
.ver{font-size:11px;font-weight:600;color:#8392ab;background:#eef1f5;border-radius:6px;padding:2px 6px;vertical-align:middle;margin-left:6px}
.top .r{margin-left:auto;display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.search{height:40px;background:#fff;border:1px solid #e0e4ec;border-radius:10px;display:flex;align-items:center;gap:8px;padding:0 12px;color:#67748e;font-size:14px;min-width:210px}
.search input{border:0;outline:0;background:transparent;font-size:14px;color:#344767;width:100%}
.seg{display:flex;background:#fff;border:1px solid #e0e4ec;border-radius:10px;overflow:hidden;height:40px}
.seg button{border:0;background:transparent;padding:0 13px;font-size:13px;font-weight:600;color:#67748e;cursor:pointer;display:flex;align-items:center;gap:6px}
.seg button.on{background:linear-gradient(195deg,#66BB6A,#43A047);color:#fff}
.iconbtn{height:40px;width:40px;border:1px solid #e0e4ec;background:#fff;border-radius:10px;color:#67748e;display:grid;place-items:center;cursor:pointer}
.iconbtn:hover{color:#43A047}
.stats{display:grid;grid-template-columns:repeat(4,1fr);gap:24px;margin:30px 0 24px}
.stat{background:#fff;border-radius:14px;box-shadow:0 4px 12px rgba(0,0,0,.06);padding:16px 18px;position:relative;min-height:100px}
.stat .box{position:absolute;top:-16px;left:18px;width:58px;height:58px;border-radius:12px;display:grid;place-items:center;color:#fff;box-shadow:0 6px 14px rgba(0,0,0,.15)}
.stat .k{text-align:right;font-size:13px;color:#67748e}.stat .v{text-align:right;font-size:26px;font-weight:700;margin-top:2px;color:#344767;line-height:1.1}
.stat .v small{font-size:14px;color:#67748e;font-weight:600}
.stat .ft{border-top:1px solid #eef0f4;margin-top:13px;padding-top:9px;font-size:12.5px;color:#67748e}.stat .ft b{color:#4CAF50}
.filters{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:16px}
.fsel{height:36px;border:1px solid #e0e4ec;background:#fff;border-radius:9px;padding:0 10px;font-size:13px;font-weight:600;color:#344767;cursor:pointer;outline:0}
.flab{font-size:12px;color:#8392ab;font-weight:600;margin-right:-4px}
.chip{height:36px;display:inline-flex;align-items:center;gap:7px;border:1px solid #e0e4ec;background:#fff;border-radius:9px;padding:0 12px;font-size:13px;font-weight:600;color:#67748e;cursor:pointer;user-select:none}
.chip.on{background:linear-gradient(195deg,#66BB6A,#43A047);color:#fff;border-color:transparent}
.chip .tk{width:8px;height:8px;border-radius:50%;background:#c7cede}.chip.on .tk{background:#fff}
.sechead{display:flex;align-items:baseline;gap:10px;margin:4px 0 16px;flex-wrap:wrap}
.sechead h2{font-size:17px;font-weight:700}.sechead small{color:#67748e;font-size:13px}
.grid{display:grid;grid-template-columns:repeat(4,1fr);gap:24px}
.grid.list{grid-template-columns:1fr}
.pc{background:#fff;border-radius:14px;box-shadow:0 4px 12px rgba(0,0,0,.06);display:flex;flex-direction:column;overflow:visible;transition:transform .15s,box-shadow .15s}
.pc:hover{transform:translateY(-2px);box-shadow:0 10px 22px rgba(0,0,0,.10)}
.grid.list .pc{flex-direction:row;align-items:stretch}
.pc .img{height:158px;margin:12px 12px 0;border-radius:12px;overflow:hidden;background:linear-gradient(160deg,#eef1ee,#dfe6e0);position:relative;display:grid;place-items:center;color:#66BB6A;flex:none}
.pc .img img{width:100%;height:100%;object-fit:cover;display:block}
.grid.list .pc .img{width:190px;height:auto;margin:12px;flex:none}
/* photo gallery navigation */
.nz{position:absolute;top:0;bottom:0;border:0;background:transparent;padding:0;z-index:2;display:grid;place-items:center;color:#fff;cursor:pointer}
.nz.left{left:0;width:34%;justify-content:flex-start}
.nz.right{right:0;width:34%;justify-content:flex-end}
.nz.mid{left:34%;width:32%;cursor:zoom-in}
.nz.mid.full{left:0;width:100%}
.nz.left>svg,.nz.right>svg{opacity:0;transition:opacity .15s;filter:drop-shadow(0 1px 3px rgba(0,0,0,.65));margin:0 5px}
.pc .img:hover .nz.left>svg,.pc .img:hover .nz.right>svg{opacity:.92}
.count{position:absolute;bottom:8px;right:8px;background:rgba(52,71,103,.85);color:#fff;font-size:11px;font-weight:600;padding:3px 8px;border-radius:20px;z-index:3;pointer-events:none}
.viewbtn{position:absolute;top:8px;right:8px;z-index:4;display:inline-flex;align-items:center;gap:5px;background:rgba(255,255,255,.94);color:#344767;font-size:11.5px;font-weight:700;padding:5px 9px;border-radius:8px;text-decoration:none;box-shadow:0 3px 8px rgba(0,0,0,.2)}
.viewbtn:hover{color:#43A047}
.bdg{position:absolute;top:8px;left:8px;background:rgba(52,71,103,.92);color:#fff;font-size:11px;font-weight:600;padding:4px 9px;border-radius:8px;display:flex;align-items:center;gap:6px;z-index:3;pointer-events:none}
.bdg .d{width:8px;height:8px;border-radius:50%}
.disc{position:absolute;top:36px;left:8px;color:#fff;font-size:12px;font-weight:700;padding:5px 9px;border-radius:8px;box-shadow:0 3px 8px rgba(0,0,0,.18);z-index:3;pointer-events:none}
.b{padding:12px 16px 16px;display:flex;flex-direction:column;gap:10px;flex:1;min-width:0}
.t{font-size:14px;font-weight:600;line-height:1.32;min-height:37px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.pstrip{display:grid;grid-template-columns:repeat(4,1fr);gap:6px;background:#f6f7f9;border-radius:11px;padding:9px 6px}
.ptile{text-align:center;position:relative;padding:0 2px}
.ptile+.ptile:before{content:'';position:absolute;left:0;top:3px;bottom:3px;width:1px;background:#e6e9f0}
.ptile .pl{font-size:9.5px;color:#8392ab;font-weight:600;letter-spacing:.1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ptile .pv{font-size:13px;font-weight:700;color:#344767;margin-top:3px}
.ptile.self .pv{font-size:16px;color:#43A047}
.ptile.na .pv{color:#b3bacb;font-weight:600}
.ptile.new{cursor:help}
.pop{display:none;position:absolute;bottom:132%;left:50%;transform:translateX(-50%);background:#344767;color:#fff;padding:9px 11px;border-radius:9px;font-size:12px;white-space:nowrap;z-index:30;box-shadow:0 8px 20px rgba(0,0,0,.25);text-align:left}
.pop:after{content:'';position:absolute;top:100%;left:50%;transform:translateX(-50%);border:6px solid transparent;border-top-color:#344767}
.ptile.new:hover .pop{display:block}
.ptile.new.edge .pop{left:auto;right:0;transform:none}.ptile.new.edge .pop:after{left:auto;right:14px;transform:none}
.pop .pt{color:#c7d0e0;font-size:10.5px;margin-bottom:2px}
.pop a{display:inline-flex;align-items:center;gap:5px;margin-top:8px;background:linear-gradient(195deg,#66BB6A,#43A047);color:#fff;padding:5px 11px;border-radius:7px;text-decoration:none;font-weight:600;font-size:11.5px}
.meta{display:flex;align-items:center;gap:8px;flex-wrap:wrap;font-size:11.5px}
.mchip{display:inline-flex;align-items:center;gap:5px;color:#67748e;font-weight:600;background:#f2f4f7;padding:3px 9px;border-radius:20px}
.mchip.mut{color:#a4adc0;background:#f6f7f9}
.mchip svg{color:#7b8ab8}
.profit{margin-top:auto;border-radius:11px;padding:9px 11px;display:flex;align-items:center;gap:10px;position:relative;cursor:default}
.profit.pos{background:#eef8ef}.profit.neg{background:#fdf0ef}.profit.none{background:#f4f5f7}
.profit .ai{width:28px;height:28px;border-radius:8px;background:linear-gradient(195deg,#66BB6A,#43A047);color:#fff;display:grid;place-items:center;font-size:11px;font-weight:800;flex:none;box-shadow:0 4px 10px rgba(67,160,71,.35)}
.profit.neg .ai{background:linear-gradient(195deg,#8a97ad,#67748e);box-shadow:none}
.profit .pt{font-size:11px;color:#67748e;line-height:1.35}.profit .pt b{font-size:15px;display:block;color:#344767}
.profit.pos .pt b{color:#43A047}.profit.neg .pt b{color:#d9534f}
.profit .cf{margin-left:auto;font-size:10px;font-weight:700;padding:3px 9px;border-radius:20px;color:#fff;text-transform:none}
.cf.hoog{background:#4CAF50}.cf.gemid{background:#fb8c00}.cf.laag{background:#8392ab}
.aipop{display:none;position:absolute;bottom:112%;left:0;right:0;background:#344767;color:#fff;padding:11px 13px;border-radius:10px;font-size:12px;line-height:1.45;z-index:30;box-shadow:0 10px 24px rgba(0,0,0,.28)}
.aipop .pt{color:#c7d0e0;font-size:10.5px;font-weight:700;letter-spacing:.4px;text-transform:uppercase;margin-bottom:4px}
.profit:hover .aipop{display:block}
.grid.list .b{flex-direction:row;align-items:center;gap:18px;flex-wrap:wrap}
.grid.list .t{min-height:0;flex:1 1 220px;-webkit-line-clamp:2}
.grid.list .pstrip{flex:1 1 300px}
.grid.list .meta{flex:0 0 auto}
.grid.list .profit{margin-top:0;flex:1 1 260px;min-width:240px}
.state{background:#fff;border-radius:14px;box-shadow:0 4px 12px rgba(0,0,0,.06);padding:48px 24px;text-align:center;color:#67748e;font-size:14px}
.state .big{font-size:15px;font-weight:700;color:#344767;margin-bottom:6px}
.skel{background:#fff;border-radius:14px;box-shadow:0 4px 12px rgba(0,0,0,.06);height:330px;overflow:hidden;position:relative}
.skel:after{content:'';position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(0,0,0,.04),transparent);animation:sh 1.3s infinite}
@keyframes sh{100%{transform:translateX(100%)}}
.foot{margin-top:26px;color:#8392ab;font-size:12.5px;display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.foot .live{width:8px;height:8px;border-radius:50%;background:#4CAF50;box-shadow:0 0 0 3px rgba(76,175,80,.2)}
/* fullscreen photo slider */
.lb{position:fixed;inset:0;background:rgba(15,20,30,.93);z-index:1000;display:grid;place-items:center;padding:40px;cursor:zoom-out;backdrop-filter:blur(2px)}
.lbimg{max-width:92vw;max-height:84vh;object-fit:contain;border-radius:8px;box-shadow:0 20px 60px rgba(0,0,0,.5);cursor:default}
.lbx{position:fixed;top:18px;right:20px;width:42px;height:42px;border-radius:50%;border:0;background:rgba(255,255,255,.14);color:#fff;display:grid;place-items:center;cursor:pointer;z-index:1002}
.lbx:hover{background:rgba(255,255,255,.28)}.lbx>svg{width:20px;height:20px}
.lbnav{position:fixed;top:50%;transform:translateY(-50%);width:52px;height:52px;border-radius:50%;border:0;background:rgba(255,255,255,.14);color:#fff;display:grid;place-items:center;cursor:pointer;z-index:1002}
.lbnav:hover{background:rgba(255,255,255,.28)}.lbnav>svg{width:26px;height:26px}
.lbnav.l{left:20px}.lbnav.r{right:20px}
.lbbar{position:fixed;bottom:20px;left:50%;transform:translateX(-50%);display:flex;align-items:center;gap:14px;background:rgba(0,0,0,.5);padding:9px 16px;border-radius:30px;color:#fff;font-size:13px;z-index:1002;max-width:92vw}
.lbtitle{font-weight:600;max-width:44vw;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.lbcount{color:#c7d0e0;font-weight:600;font-size:12px}
.lbview{display:inline-flex;align-items:center;gap:6px;background:linear-gradient(195deg,#66BB6A,#43A047);color:#fff;padding:6px 13px;border-radius:20px;text-decoration:none;font-weight:700;font-size:12.5px;white-space:nowrap}
@media(max-width:1280px){.grid{grid-template-columns:repeat(3,1fr)}}
@media(max-width:1080px){.stats{grid-template-columns:repeat(2,1fr);row-gap:34px}.grid{grid-template-columns:repeat(2,1fr)}}
@media(max-width:820px){.side{display:none}.main{margin-left:0;padding:16px}}
@media(max-width:560px){.grid{grid-template-columns:1fr}.stats{grid-template-columns:1fr;row-gap:32px}.top .r{width:100%}.search{flex:1}.lbtitle{max-width:30vw}}
`

function SourceBadge({ s }: { s?: string | null }) {
  return (
    <span className="bdg"><span className="d" style={{ background: isMkt(s) ? '#fb8c00' : '#4CAF50' }} />{srcShort(s)}</span>
  )
}

function PriceTile({ label, value, cls = '' }: { label: string; value?: number | null; cls?: string }) {
  const v = eur(value)
  return (
    <div className={`ptile ${cls} ${v == null ? 'na' : ''}`}>
      <div className="pl">{label}</div>
      <div className="pv">{v ?? 'n.v.t.'}</div>
    </div>
  )
}

function NewPriceTile({ it }: { it: Item }) {
  const v = eur(it.new_price)
  return (
    <div className={`ptile new edge ${v == null ? 'na' : ''}`}>
      <div className="pl">nieuwprijs</div>
      <div className="pv">{v ?? 'n.v.t.'}</div>
      <div className="pop">
        {it.new_price != null ? (
          <>
            <div className="pt">Nieuwprijs{it.new_price_source ? ' · ' + it.new_price_source : ''}</div>
            {v}
            {it.retailer_url && (
              <div><a href={it.retailer_url} target="_blank" rel="noreferrer">naar webshop ↗</a></div>
            )}
          </>
        ) : (
          <>Geen nieuwprijs bekend</>
        )}
      </div>
    </div>
  )
}

function Card({ it, onOpen }: { it: Item; onOpen: (lb: LB) => void }) {
  const disc = it.discount_pct ?? 0
  const profit = it.ai_profit
  const pcls = profit == null ? 'none' : profit > 0 ? 'pos' : 'neg'
  const ck = confKey(it.ai_confidence)
  const price = it.price != null && it.price > 0 ? eur(it.price) : '—'

  const imgs = useMemo(() => galleryOf(it), [it])
  const [i, setI] = useState(0)
  const cur = imgs.length ? Math.min(i, imgs.length - 1) : 0
  const many = imgs.length > 1
  const prev = () => setI((p) => (p - 1 + imgs.length) % imgs.length)
  const next = () => setI((p) => (p + 1) % imgs.length)
  const open = () => imgs.length && onOpen({ imgs, i: cur, title: it.title, url: it.url })

  return (
    <div className="pc">
      <div className="img">
        {imgs.length ? (
          <img src={imgs[cur]} alt={it.title} loading="lazy"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }} />
        ) : Ic.tag}

        {imgs.length > 0 && (many ? (
          <>
            <button className="nz left" aria-label="vorige foto"
              onClick={(e) => { e.stopPropagation(); prev() }}>{Ic.chevL}</button>
            <div className="nz mid" title="Foto vergroten" onClick={open} />
            <button className="nz right" aria-label="volgende foto"
              onClick={(e) => { e.stopPropagation(); next() }}>{Ic.chevR}</button>
            <span className="count">{cur + 1}/{imgs.length}</span>
          </>
        ) : (
          <div className="nz mid full" title="Foto vergroten" onClick={open} />
        ))}

        <SourceBadge s={it.source_name} />
        {disc > 0 && (
          <span className="disc" style={{ background: disc >= 33 ? 'linear-gradient(195deg,#EF5350,#E53935)' : 'linear-gradient(195deg,#FFA726,#FB8C00)' }}>−{disc}%</span>
        )}
        <a className="viewbtn" href={it.url} target="_blank" rel="noreferrer"
          title="Naar het originele zoekertje" onClick={(e) => e.stopPropagation()}>{Ic.ext} Bekijk</a>
      </div>
      <div className="b">
        <a className="t" href={it.url} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>{it.title}</a>
        <div className="pstrip">
          <div className="ptile self">
            <div className="pl">dit listing</div>
            <div className="pv">{price}</div>
          </div>
          <PriceTile label="gem. 2dehands" value={it.avg_2dehands} />
          <PriceTile label="gem. mktpl." value={it.avg_marktplaats} />
          <NewPriceTile it={it} />
        </div>
        <div className="meta">
          {it.distance_text ? (
            <span className="mchip" title="Rijafstand vanaf 9100 Sint-Niklaas">{Ic.car}{it.distance_text}</span>
          ) : (
            <span className="mchip mut" title="Rijafstand vanaf 9100 Sint-Niklaas">{Ic.car}onbekend</span>
          )}
          {it.condition && condLabel[it.condition] && <span className="mchip">{condLabel[it.condition]}</span>}
          {it.location && <span className="mchip mut" style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.location}</span>}
        </div>
        <div className={`profit ${pcls}`}>
          <div className="ai">AI</div>
          <div className="pt">Geschatte flip-winst
            <b>{profit == null ? 'geen schatting' : (profit > 0 ? '+' : '') + eur(profit) + ' na kosten'}</b>
          </div>
          {ck && <span className={`cf ${ck}`}>{confLabel[ck]}</span>}
          {it.ai_reason && (
            <div className="aipop"><div className="pt">AI-analyse</div>{it.ai_reason}</div>
          )}
        </div>
      </div>
    </div>
  )
}

function Lightbox({ lb, set }: { lb: LB; set: (l: LB | null) => void }) {
  const many = lb.imgs.length > 1
  const go = (d: number) => set({ ...lb, i: (lb.i + d + lb.imgs.length) % lb.imgs.length })
  return (
    <div className="lb" onClick={() => set(null)}>
      <button className="lbx" aria-label="sluiten" onClick={(e) => { e.stopPropagation(); set(null) }}>{Ic.close}</button>
      {many && <button className="lbnav l" aria-label="vorige" onClick={(e) => { e.stopPropagation(); go(-1) }}>{Ic.chevL}</button>}
      <img className="lbimg" src={lb.imgs[lb.i]} alt={lb.title} onClick={(e) => e.stopPropagation()} />
      {many && <button className="lbnav r" aria-label="volgende" onClick={(e) => { e.stopPropagation(); go(1) }}>{Ic.chevR}</button>}
      <div className="lbbar" onClick={(e) => e.stopPropagation()}>
        <span className="lbtitle">{lb.title}</span>
        {many && <span className="lbcount">{lb.i + 1} / {lb.imgs.length}</span>}
        <a className="lbview" href={lb.url} target="_blank" rel="noreferrer">{Ic.ext} Bekijk zoekertje</a>
      </div>
    </div>
  )
}

function Stat({ icon, bg, k, v, ft }: { icon: React.ReactNode; bg: string; k: string; v: React.ReactNode; ft: React.ReactNode }) {
  return (
    <div className="stat">
      <div className="box" style={{ background: bg }}>{icon}</div>
      <div className="k">{k}</div>
      <div className="v">{v}</div>
      <div className="ft">{ft}</div>
    </div>
  )
}

export type FestoolAppProps = {
  keycloakToken?: string
  keycloakUser?: {
    sub: string
    preferred_username: string
    email?: string
    roles: string[]
  }
  basePath?: string
  apiBaseUrl?: string
}

export default function App({
  keycloakToken,
  keycloakUser,
  basePath,
  apiBaseUrl = DEFAULT_API,
}: FestoolAppProps = {}) {
  void keycloakUser
  void basePath

  const API = (apiBaseUrl || DEFAULT_API).replace(/\/+$/, '')

  // The API requires a Keycloak token with the `admin` role on every route.
  // Prefer the shell's localStorage copy (refreshed) and fall back to the prop,
  // which is the only source when the shell logs in through the OIDC redirect
  // flow rather than the direct grant.
  const authHeaders = useCallback((): HeadersInit => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY) || keycloakToken
    return token ? { Authorization: `Bearer ${token}` } : {}
  }, [keycloakToken])

  const [items, setItems] = useState<Item[]>([])
  const [total, setTotal] = useState(0)
  const [stats, setStats] = useState<Stats>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [view, setView] = useState<'thumb' | 'list'>('thumb')
  const [sort, setSort] = useState<Sort>('profit')
  const [source, setSource] = useState('')
  const [onlyProfit, setOnlyProfit] = useState(false)
  const [minDiscount, setMinDiscount] = useState(0)
  const [search, setSearch] = useState('')
  const [reload, setReload] = useState(0)
  const [lb, setLb] = useState<LB | null>(null)

  useEffect(() => {
    fetch(`${API}/api/stats`, { headers: authHeaders() })
      .then((r) => { if (!r.ok) throw new Error(); return r.json() })
      .then(setStats)
      .catch(() => {})
  }, [API, authHeaders])

  useEffect(() => {
    setLoading(true); setError(false)
    const qs = new URLSearchParams({ sort, page_size: '100' })
    if (source) qs.set('source', source)
    fetch(`${API}/api/items?${qs}`, { headers: authHeaders() })
      .then((r) => { if (!r.ok) throw new Error(); return r.json() })
      .then((d) => { setItems(d.items || []); setTotal(d.total ?? (d.items || []).length) })
      .catch(() => { setItems([]); setError(true) })
      .finally(() => setLoading(false))
  }, [API, authHeaders, sort, source, reload])

  // Keyboard control for the fullscreen slider: ← / → navigate, Esc closes.
  useEffect(() => {
    if (!lb) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLb(null)
      else if (e.key === 'ArrowLeft') setLb((p) => (p ? { ...p, i: (p.i - 1 + p.imgs.length) % p.imgs.length } : p))
      else if (e.key === 'ArrowRight') setLb((p) => (p ? { ...p, i: (p.i + 1) % p.imgs.length } : p))
    }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = '' }
  }, [lb])

  const shown = useMemo(() => {
    let arr = items
    if (onlyProfit) arr = arr.filter((i) => (i.ai_profit ?? -Infinity) > 0)
    if (minDiscount > 0) arr = arr.filter((i) => (i.discount_pct ?? 0) >= minDiscount)
    const q = search.trim().toLowerCase()
    if (q) arr = arr.filter((i) => (i.title || '').toLowerCase().includes(q))
    return arr
  }, [items, onlyProfit, minDiscount, search])

  const best = useMemo(() => {
    let b: Item | null = null
    for (const it of items) if (it.ai_profit != null && (b == null || it.ai_profit > (b.ai_profit || 0))) b = it
    return b
  }, [items])

  const sortLabel: Record<Sort, string> = { profit: 'gesorteerd op hoogste geschatte winst', distance: 'gesorteerd op kortste rijafstand', recent: 'gesorteerd op recentst gezien' }

  return (
    <div className="ftl-app">
      <style>{CSS}</style>
      <div className="layout">
        <aside className="side">
          <div className="brand"><div className="lg">F</div><div><b>FESTOOL</b><small>Monitor · BE/NL</small></div></div>
          <nav className="nav">{nav.map(([label, icon, on]) => (
            <a key={label as string} className={on ? 'on' : ''}><span className="ib">{icon}</span>{label as string}</a>
          ))}</nav>
        </aside>
        <main className="main">
          <div className="top">
            <div><div className="crumb">Pages / <b>Dashboard</b></div><div className="h1">Festool Monitor <span className="ver">v{__APP_VERSION__}</span></div></div>
            <div className="r">
              <div className="search">{Ic.search}<input placeholder="Zoek model…" value={search} onChange={(e) => setSearch(e.target.value)} /></div>
              <div className="seg">
                <button className={view === 'thumb' ? 'on' : ''} onClick={() => setView('thumb')}>{Ic.grid} Thumbnails</button>
                <button className={view === 'list' ? 'on' : ''} onClick={() => setView('list')}>{Ic.rows} Lijst</button>
              </div>
              <button className="iconbtn" title="Vernieuwen" onClick={() => setReload((r) => r + 1)}>{Ic.refresh}</button>
            </div>
          </div>

          <div className="stats">
            <Stat icon={Ic.layers} bg="linear-gradient(195deg,#42424a,#191919)" k="Actieve listings"
              v={stats.active_listings ?? '—'} ft={<>bron: {(stats.sources || ['2dehands.be', 'marktplaats.nl']).join(' + ')}</>} />
            <Stat icon={Ic.box} bg="linear-gradient(195deg,#66BB6A,#43A047)" k="Modellen in catalogus"
              v={stats.catalog_models ?? '—'} ft={<>herkende Festool-modellen</>} />
            <Stat icon={Ic.euro} bg="linear-gradient(195deg,#49a3f1,#1A73E8)" k="Geprijsd"
              v={stats.priced_models ?? '—'} ft={<><b>{stats.catalog_models ? Math.round(100 * (stats.priced_models || 0) / stats.catalog_models) + '%' : '—'}</b> met nieuwprijs</>} />
            <Stat icon={Ic.zap} bg="linear-gradient(195deg,#FFA726,#FB8C00)" k="Beste flip-kans"
              v={best?.ai_profit != null ? '+' + eur(best.ai_profit) : '—'}
              ft={best ? <>{prettyModel(best.canonical_model_key) || 'Festool'} · {best.ai_confidence || '—'}</> : <>AI-schatting</>} />
          </div>

          <div className="filters">
            <span className="flab">Sorteer</span>
            <select className="fsel" value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
              <option value="profit">op winst</option>
              <option value="distance">op afstand</option>
              <option value="recent">recentst</option>
            </select>
            <span className="flab">Bron</span>
            <select className="fsel" value={source} onChange={(e) => setSource(e.target.value)}>
              <option value="">alle bronnen</option>
              <option value="2dehands.be">2dehands.be</option>
              <option value="marktplaats.nl">marktplaats.nl</option>
            </select>
            <span className="flab">Min. korting</span>
            <select className="fsel" value={minDiscount} onChange={(e) => setMinDiscount(Number(e.target.value))}>
              <option value={0}>alle</option>
              <option value={10}>≥ 10%</option>
              <option value={20}>≥ 20%</option>
              <option value={30}>≥ 30%</option>
            </select>
            <span className={`chip ${onlyProfit ? 'on' : ''}`} onClick={() => setOnlyProfit((v) => !v)}>
              <span className="tk" />alleen met winst
            </span>
          </div>

          <div className="sechead">
            <h2>Listings</h2>
            <small>{loading ? 'laden…' : `${shown.length} van ${total} — ${sortLabel[sort]}`}</small>
          </div>

          {loading ? (
            <div className="grid">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="skel" />)}</div>
          ) : error ? (
            <div className="state"><div className="big">Kon listings niet laden</div>De API is momenteel niet bereikbaar. Probeer te vernieuwen.</div>
          ) : shown.length === 0 ? (
            <div className="state"><div className="big">Geen listings gevonden</div>Geen resultaten voor de huidige filters.</div>
          ) : (
            <div className={`grid ${view === 'list' ? 'list' : ''}`}>
              {shown.map((it) => <Card key={it.id} it={it} onOpen={setLb} />)}
            </div>
          )}

          <div className="foot"><span className="live" />Crawler 6×/dag gespreid 07:00–22:00 · Rijafstand vanaf 9100 Sint-Niklaas · Bronnen: {(stats.sources || ['2dehands.be', 'marktplaats.nl']).join(' + ')}</div>
        </main>
      </div>
      {lb && <Lightbox lb={lb} set={setLb} />}
    </div>
  )
}
